import React, { ReactElement, useCallback, useMemo, useRef } from 'react';
import { View, StyleSheet, ViewToken } from 'react-native';
import { useAppNavigation } from '_navigator';
import { useKeyboardIsVisible, WH, WW } from '_utils';
import { track } from '_utils/Amplitude';
import { useBottomSpacer } from '_utils/hooks';
import { screenMargin } from '_utils/sizes';
import { EmptyMessage, QueryView } from 'Components';
import ImageCardButton from 'Components/ImageCardButton';
import { QueryViewProps } from 'Components/QueryViews/QueryView';
import { HelpfulNeighborTag, Message } from 'types/message';
import MessageItem from './MessageItem/MessageItem';

interface MessageListViewProps
    extends Omit<
        QueryViewProps<Message>,
        | 'renderItem'
        | 'style'
        | 'containerStyle'
        | 'columnWrapperStyle'
        | 'emptyList'
        | 'flashlist'
        | 'onViewableItemsChanged'
    > {
    emptyText: string;
    showActionButton: boolean;
    actionButton?: ReactElement;
    numColumns: number;
    preferableCategories?: Array<HelpfulNeighborTag>;
    onMessagesViewed: (messages: Array<Message>) => void;
    displayBookingSharing?: boolean;
    parent: 'neighborSharing' | 'sharableItems' | 'helpfulNeighbors';
}

const estimatedItemSize = WH * 0.5;

type listItem = Message | { type: 'bookingSharing'; id: 'parking' };

const MessageListView = ({
    parent,
    emptyText,
    numColumns,
    showActionButton,
    actionButton,
    onMessagesViewed,
    data,
    preferableCategories,
    displayBookingSharing,
    ...props
}: MessageListViewProps): ReactElement => {
    const { navigate } = useAppNavigation();
    const keyboardIsVisible = useKeyboardIsVisible();
    const paddingBottom = useBottomSpacer();
    const visibleItems = useRef<Set<number>>(new Set());

    const isBookingSharing = (item: listItem): item is { type: 'bookingSharing'; id: 'parking' } =>
        item.type === 'bookingSharing';

    const onViewableItemsChanged = useCallback(
        (info: { viewableItems: Array<ViewToken>; changed: Array<ViewToken> }) => {
            const messages = info.viewableItems
                .map(({ item }) => item)
                .filter((item): item is Message => !isBookingSharing(item));
            // Update visible items set
            const newVisibleItems = new Set(messages.map((item) => item.id));
            visibleItems.current = newVisibleItems;

            // Call the original onViewableItemsChanged from props if it exists
            onMessagesViewed(messages);
        },
        [onMessagesViewed],
    );

    const renderItem = useCallback(
        ({ item, index }: { item: listItem; index: number }) => {
            if (isBookingSharing(item)) {
                const handlePress = () => {
                    track(
                        parent === 'neighborSharing'
                            ? 'Pressed parking sharing in sharing'
                            : 'Pressed parking sharing in reservations',
                    );
                    navigate('BookingSharingCategory', undefined);
                };
                return <ImageCardButton type="parking" communityLabel onPress={handlePress} key={item.id} />;
            }
            const onPress = (expanded: boolean) => {
                navigate('SharingSelected', {
                    messageId: item.id,
                    typeHint: item.type,
                    expanded: expanded ? 'true' : 'false',
                });
            };

            return (
                <MessageItem
                    isFirst={index === 0}
                    item={item}
                    onPress={onPress}
                    isVisible={visibleItems.current.has(item.id)}
                    preferableCategories={preferableCategories}
                />
            );
        },
        [preferableCategories, parent, navigate],
    );

    const dataWithBookingSharing = useMemo((): Array<listItem> => {
        if (displayBookingSharing) {
            return [...data, { type: 'bookingSharing' as const, id: 'parking' }];
        }
        return data;
    }, [data, displayBookingSharing]);

    return (
        <>
            <QueryView<listItem>
                {...props}
                data={dataWithBookingSharing}
                renderItem={renderItem}
                onViewableItemsChanged={onViewableItemsChanged}
                columnWrapperStyle={numColumns && numColumns > 1 ? styles.columnWrapperStyle : undefined}
                emptyList={
                    <View style={styles.emptyContainer}>
                        <EmptyMessage message={emptyText} />
                    </View>
                }
                {...(numColumns > 1
                    ? {
                          flashlist: false,
                          numColumns: numColumns,
                          contentContainerStyle: [
                              numColumns && numColumns > 1 ? styles.containerStyleColumns : undefined,
                              { paddingBottom },
                          ],
                          style: styles.verticalPadding,
                      }
                    : {
                          flashlist: true,
                          estimatedItemSize: estimatedItemSize,
                          contentContainerStyle: { paddingBottom },
                      })}
            />
            {keyboardIsVisible || !showActionButton ? null : actionButton}
        </>
    );
};

const styles = StyleSheet.create({
    verticalPadding: {
        paddingBottom: WW * 0.02,
    },
    containerStyleColumns: {
        paddingLeft: screenMargin,
        paddingRight: screenMargin,
    },
    columnWrapperStyle: {
        justifyContent: 'space-between',
        alignItems: 'stretch',
    },
    emptyContainer: {
        marginTop: screenMargin,
        marginRight: screenMargin,
        marginBottom: screenMargin,
        marginLeft: screenMargin,
    },
});

export default MessageListView;
